import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Zap, TrendingUp, Check, Star } from 'lucide-react';
import { PLANS } from '../lib/stripe';

const handleRenewalClick2 = (url: string) => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = url;
};

const Home = () => {
  useEffect(() => {
    if ('serviceWorker' in navigator && !sessionStorage.getItem('swCleared')) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        regs.forEach(reg => reg.unregister());
        sessionStorage.setItem('swCleared', 'true');
      });
    }
  }, []);

  return (
    <div className="relative overflow-hidden">
      <main>
        {/* Hero section */}
        <div className="pt-10 bg-gray-900 sm:pt-16 lg:pt-8 lg:pb-14 lg:overflow-hidden">
          <div className="mx-auto max-w-7xl lg:px-8">
            <div className="lg:grid lg:grid-cols-2 lg:gap-8">
              <div className="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 sm:text-center lg:px-0 lg:text-left lg:flex lg:items-center">
                <div className="lg:py-24">
                  <h1 className="mt-4 text-4xl tracking-tight font-extrabold text-white sm:mt-5 sm:text-6xl lg:mt-6 xl:text-6xl">
                    <span className="block">Geração de Leads</span>
                    <span className="block text-indigo-400">com Inteligência Artificial</span>
                  </h1>
                  <p className="mt-3 text-base text-gray-300 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl">
                    TESTE GRÁTIS (Sem cartão)! Experimente por 3 dias a Aura IA: gere leads qualificados, descubra seu público ideal, otimize a prospecção B2B e tenha segmentação inteligente, análise preditiva e chat ilimitado.
                  </p>
                  <div className="mt-2 flex flex-col items-center">
                  <Link
                    to="/register"
                    className="px-8 py-3 text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md text-center"
                  >
                    Teste Gratuito por 3 dias
                  </Link>
                  <p className="mt-2 text-sm text-indigo-200 font-medium animate-pulse">
                    Sem a necessidade de cartão de crédito
                  </p>
                </div>
                </div>
              </div>
              <div className="mt-12 -mb-16 sm:-mb-48 lg:m-0 lg:relative">
                <div className="mx-auto max-w-md px-4 sm:max-w-2xl sm:px-6 lg:max-w-none lg:px-0">
                  <img
                    className="w-full lg:absolute lg:inset-y-0 lg:left-0 lg:h-full lg:w-auto lg:max-w-none rounded-lg"
                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80"
                    alt="Marketing Digital e IA"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing section */}
        <div className="bg-gray-100 dark:bg-gray-900 py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="text-base font-semibold leading-7 text-indigo-600">Preços</h2>
              <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
                Pague uma vez, renove apenas se quiser
              </p>
              <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
                Modelo pré-pago sem assinatura obrigatória
              </p>
            </div>
            <div id="pricing-section" className="mt-16 grid grid-cols-1 gap-8 sm:mt-20 sm:grid-cols-2 lg:grid-cols-4">
              {/* Trial Plan */}
              <div className="relative flex flex-col rounded-2xl border border-indigo-600 bg-white dark:bg-gray-800 p-8 shadow-sm">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-indigo-600 px-4 py-1 text-sm font-semibold text-white">
                  Inicial
                </div>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold leading-8 text-gray-900 dark:text-white">{PLANS.TRIAL.name}</h3>
                  <p className="mt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">R$ 7,47 por 7 dias</p>
                </div>
                <ul className="mt-6 space-y-4 flex-1">
                  {PLANS.TRIAL.features.map((feature) => (
                    <li key={feature} className="flex gap-3">
                      <Check className="h-6 w-6 flex-none text-indigo-600" />
                      <span className="text-sm leading-6 text-gray-600 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`https://buy.stripe.com/8wM7vo26i4Or9bi3cc`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 block rounded-md bg-indigo-600 px-3.5 py-2 text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Começar agora
                </Link>
              </div>

              {/* Essential Plan */}
              <div className="flex flex-col rounded-2xl border border-gray-200 bg-white dark:bg-gray-800 p-8 shadow-sm">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold leading-8 text-gray-900 dark:text-white">{PLANS.ESSENTIAL.name}</h3>
                  <p className="mt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">R$ {PLANS.ESSENTIAL.price} por 30 dias</p>
                </div>
                <ul className="mt-6 space-y-4 flex-1">
                  {PLANS.ESSENTIAL.features.map((feature) => (
                    <li key={feature} className="flex gap-3">
                      <Check className="h-6 w-6 flex-none text-indigo-600" />
                      <span className="text-sm leading-6 text-gray-600 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`https://buy.stripe.com/6oE3f826igx90EMaEO`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 block rounded-md bg-indigo-600 px-3.5 py-2 text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Comprar Essencial
                </Link>
              </div>

              {/* Pro Plan */}
              <div className="relative flex flex-col rounded-2xl border border-gray-200 bg-white dark:bg-gray-800 p-8 shadow-sm">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-indigo-600 px-4 py-1 text-sm font-semibold text-white">
                  Mais Popular
                </div>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold leading-8 text-gray-900 dark:text-white">{PLANS.PRO.name}</h3>
                  <p className="mt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">R$ {PLANS.PRO.price} por 30 dias</p>
                </div>
                <ul className="mt-6 space-y-4 flex-1">
                  {PLANS.PRO.features.map((feature) => (
                    <li key={feature} className="flex gap-3">
                      <Check className="h-6 w-6 flex-none text-indigo-600" />
                      <span className="text-sm leading-6 text-gray-600 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`https://buy.stripe.com/4gweXQcKW4OrevC5kt`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 block rounded-md bg-indigo-600 px-3.5 py-2 text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Comprar PRO
                </Link>
              </div>

              {/* Annual Plan */}
              <div className="flex flex-col rounded-2xl border border-gray-200 bg-white dark:bg-gray-800 p-8 shadow-sm">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold leading-8 text-gray-900 dark:text-white">{PLANS.ANNUAL.name}</h3>
                  <p className="mt-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">R$ {PLANS.ANNUAL.price} por 365 dias</p>
                </div>
                <ul className="mt-6 space-y-4 flex-1">
                  {PLANS.ANNUAL.features.map((feature) => (
                    <li key={feature} className="flex gap-3">
                      <Check className="h-6 w-6 flex-none text-indigo-600" />
                      <span className="text-sm leading-6 text-gray-600 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`https://buy.stripe.com/bIYg1UcKW4Or73acMX`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 block rounded-md bg-indigo-600 px-3.5 py-2 text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                >
                  Comprar ANUAL
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Feature section */}
        <div className="relative bg-white dark:bg-gray-900 py-16 sm:py-24 lg:py-32">
          <div className="mx-auto max-w-md px-4 text-center sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl">
            <h2 className="text-base font-semibold tracking-wider text-indigo-600 uppercase">Tudo que você precisa</h2>
            <p className="mt-2 text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight sm:text-4xl">
              Segmentação inteligente para melhores resultados
            </p>
            <div className="mt-12">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                <div className="pt-6">
                  <div className="flow-root bg-gray-50 dark:bg-gray-800 rounded-lg px-6 pb-8">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-indigo-500 rounded-md shadow-lg">
                          <Target className="h-6 w-6 text-white" />
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white tracking-tight">Segmentação Precisa</h3>
                      <p className="mt-5 text-base text-gray-500 dark:text-gray-400">
                        Receba sugestões baseadas em IA para palavras-chave, interesses e dados demográficos que combinam com seu perfil de cliente ideal.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-6">
                  <div className="flow-root bg-gray-50 dark:bg-gray-800 rounded-lg px-6 pb-8">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-indigo-500 rounded-md shadow-lg">
                          <Users className="h-6 w-6 text-white" />
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white tracking-tight">Públicos Semelhantes</h3>
                      <p className="mt-5 text-base text-gray-500 dark:text-gray-400">
                        Descubra novos clientes potenciais com sugestões avançadas de públicos semelhantes baseados em seus melhores resultados.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-6">
                  <div className="flow-root bg-gray-50 dark:bg-gray-800 rounded-lg px-6 pb-8">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-indigo-500 rounded-md shadow-lg">
                          <TrendingUp className="h-6 w-6 text-white" />
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white tracking-tight">Insights de Performance</h3>
                      <p className="mt-5 text-base text-gray-500 dark:text-gray-400">
                        Acompanhe e otimize suas estratégias de segmentação com análises detalhadas de desempenho e recomendações.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12">
              <Link
                to="/register"
                className="inline-block px-8 py-3 text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md text-center"
              >
                Comece Agora seu Teste Gratuito de 03 Dias
              </Link>
              <p className="mt-2 text-center text-sm text-indigo-600 font-medium">
                Sem a necessidade de cartão de crédito
              </p>
            </div>
          </div>
        </div>

        {/* Demo Video Section */}
        <div className="bg-gray-100 dark:bg-gray-900 py-16 sm:py-24 lg:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="text-base font-semibold leading-7 text-indigo-600">Demonstração</h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                Veja o sistema em ação
              </p>
              <div className="mt-10 aspect-w-16 aspect-h-9">
                <iframe 
                  className="w-full h-96 rounded-lg shadow-xl" 
                  src="https://www.youtube.com/embed/Ebf8nPo97C0" 
                  title="Demonstração do Sistema" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen>
                </iframe>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="bg-white dark:bg-gray-900 py-16 sm:py-24 lg:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <h2 className="text-base font-semibold leading-7 text-indigo-600">Depoimentos</h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
                O que nossos clientes dizem
              </p>
            </div>
            <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {/* Testimonial 1 */}
              <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-600 dark:text-gray-300">
                  <p>"A Aura AI revolucionou minha forma de vender Tupperware! As sugestões de segmentação são incríveis e o chat integrado me ajuda a tirar dúvidas em tempo real. Minhas vendas aumentaram 65% em 2 meses."</p>
                </blockquote>
                <div className="mt-6 flex items-center">
                  <div className="ml-4">
                    <div className="font-medium text-gray-900 dark:text-white">Rosemeire Ferreira</div>
                    <div className="text-indigo-600">Consultora de Tupperware</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Ituiutaba-MG</div>
                  </div>
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-600 dark:text-gray-300">
                  <p>"Como loja de pratas, precisávamos atingir o público certo. A Aura AI nos mostrou segmentações que nunca havíamos pensado. O melhor é poder conversar com a IA quando precisamos de ajuda com estratégias de marketing."</p>
                </blockquote>
                <div className="mt-6 flex items-center">
                  <div className="ml-4">
                    <div className="font-medium text-gray-900 dark:text-white">Izabella Azevedo</div>
                    <div className="text-indigo-600">Proprietária - Aura de Prata</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Prata-MG</div>
                  </div>
                </div>
              </div>

              {/* Testimonial 3 */}
              <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-600 dark:text-gray-300">
                  <p>"Na corretora, tempo é dinheiro. A Aura AI nos economiza horas de pesquisa com suas sugestões precisas. O chat integrado é como ter uma consultora de marketing 24/7. Vale cada centavo investido!"</p>
                </blockquote>
                <div className="mt-6 flex items-center">
                  <div className="ml-4">
                    <div className="font-medium text-gray-900 dark:text-white">Carlos Mendonça</div>
                    <div className="text-indigo-600">Corretor de Imóveis</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Uberlândia-MG</div>
                  </div>
                </div>
              </div>

              {/* Testimonial 4 */}
              <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-lg shadow-sm sm:hidden lg:block">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-600 dark:text-gray-300">
                  <p>"No hospital, precisamos comunicar serviços especializados. A Aura AI nos ajuda a segmentar por condições de saúde e perfis demográficos. A IA integrada responde dúvidas técnicas sobre marketing em saúde."</p>
                </blockquote>
                <div className="mt-6 flex items-center">
                  <div className="ml-4">
                    <div className="font-medium text-gray-900 dark:text-white">Dra. Ana Lúcia Torres</div>
                    <div className="text-indigo-600">Diretora de Marketing Hospitalar</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Belo Horizonte-MG</div>
                  </div>
                </div>
              </div>

              {/* Testimonial 5 */}
              <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-lg shadow-sm sm:hidden lg:block">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-600 dark:text-gray-300">
                  <p>"Como contador, sempre tive dificuldade com marketing digital. A Aura AI simplificou tudo, mostrando exatamente como segmentar para pequenos empresários. O chat responde até dúvidas sobre impostos em anúncios!"</p>
                </blockquote>
                <div className="mt-6 flex items-center">
                  <div className="ml-4">
                    <div className="font-medium text-gray-900 dark:text-white">Marcos Vinicius</div>
                    <div className="text-indigo-600">Contador</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">São Paulo-SP</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Home;